import {
  createProductVariant as createProductVariantApi,
  deleteProductVariant as deleteProductVariantApi,
  getAll,
  updateProductVariant as updateProductVariantApi
} from "@/api/product-variant-api";
import {
  buildCacheKey,
  useCollectionData,
  useMutation
} from "@/hooks/use-api";
import {
  ProductVariant,
  ProductVariantCreateFormValues,
  ProductVariantCreateRequest,
  ProductVariantUpdateFormValues,
  ProductVariantUpdateRequest
} from "@/types/product-variant-type";
import { mutate } from "swr";

// Constants
const PRODUCT_VARIANTS_ENDPOINT = "/product-variants";
const PRODUCTS_ENDPOINT = "/products";

// Hook for getting product variants by product ID
// export function useProductVariantByProductId(
//   productId: string | null,
//   params?: {
//     page?: number;
//     size?: number;
//     sort?: ProductVariantSortOption;
//     filters?: ProductVariantFilters;
//   }
// ) {
//   const cacheKey = productId
//     ? buildCacheKey(`${PRODUCTS_ENDPOINT}/${productId}/variants`, params)
//     : null;

//   return useCollectionData(
//     cacheKey,
//     productId
//       ? () => getProductVariants(productId)
//       : () => Promise.resolve({ data: [] } as any),
//     {
//       refreshInterval: 30000,
//       revalidateOnFocus: true,
//     }
//   );
// }

export function useProductVariants(
  options?: {
    refreshInterval?: number;
    revalidateOnFocus?: boolean;
  }
) {
  return useCollectionData(
    buildCacheKey(PRODUCT_VARIANTS_ENDPOINT),
    () => getAll(),
    {
      refreshInterval: options?.refreshInterval ?? 30000,
      revalidateOnFocus: options?.revalidateOnFocus ?? true,
    }
  );
}

// Hook for creating a product variant
export function useCreateProductVariant() {
  return useMutation(
    (data: ProductVariantCreateFormValues) => {
      const requestData: ProductVariantCreateRequest = {
        storeId: data.storeId,
        productId: data.productId,
        sku: data.sku,
        name: data.name,
        price: data.price,
        cost: data.cost,
        imageUrl: data.imageUrl,
        color: data.color ?? null,
        size: data.size ?? null,
      };
      return createProductVariantApi(requestData);
    },
    {
      onSuccess: async (response, variables) => {
        console.log("Product variant created successfully:", response.data);
        // Invalidate relevant cache keys
        await Promise.all([
          mutate(`${PRODUCTS_ENDPOINT}/${variables.productId}/variants`),
          mutate(`${PRODUCTS_ENDPOINT}/${variables.productId}/relation`),
        ]);
      },
      onError: (error) => {
        console.error("Error creating product variant:", error);
      },
      invalidateKeys: [PRODUCT_VARIANTS_ENDPOINT],
    }
  );
}

// Hook for updating a product variant
export function useUpdateProductVariant() {
  return useMutation(
    ({
      id,
      data,
    }: {
      id: string;
      data: ProductVariantUpdateFormValues;
      productId?: string;
    }) => {
      const requestData: ProductVariantUpdateRequest = {
        sku: data.sku,
        name: data.name,
        price: data.price,
        cost: data.cost,
        imageUrl: data.imageUrl,
        color: data.color ?? null,
        size: data.size ?? null,
      };
      return updateProductVariantApi(id, requestData);
    },
    {
      onSuccess: async (response, variables) => {
        console.log("Product variant updated successfully:", response.data);
        // Invalidate relevant cache keys
        if (variables.productId) {
          await Promise.all([
            mutate(`${PRODUCTS_ENDPOINT}/${variables.productId}/variants`),
            mutate(`${PRODUCTS_ENDPOINT}/${variables.productId}/relation`),
          ]);
        }
      },
      onError: (error) => {
        console.error("Error updating product variant:", error);
      },
      invalidateKeys: [PRODUCT_VARIANTS_ENDPOINT],
    }
  );
}

// Hook for deleting a product variant
export function useDeleteProductVariant() {
  return useMutation(
    ({ id, productId }: { id: string; productId?: string }) =>
      deleteProductVariantApi(id),
    {
      onSuccess: async (response, variables) => {
        console.log("Product variant deleted successfully:", response.data);
        // Invalidate relevant cache keys
        if (variables.productId) {
          await Promise.all([
            mutate(`${PRODUCTS_ENDPOINT}/${variables.productId}/variants`),
            mutate(`${PRODUCTS_ENDPOINT}/${variables.productId}/relation`),
          ]);
        }
      },
      onError: (error) => {
        console.error("Error deleting product variant:", error);
      },
      invalidateKeys: [PRODUCT_VARIANTS_ENDPOINT],
    }
  );
}

// Utility functions for product variant operations
export const productVariantService = {
  // Filter variants by search query (client-side)
  filterVariants: (
    variants: ProductVariant[],
    query: string
  ): ProductVariant[] => {
    if (!query.trim()) return variants;

    const lowerQuery = query.toLowerCase();
    return variants.filter(
      (variant) =>
        variant.name.toLowerCase().includes(lowerQuery) ||
        variant.sku.toLowerCase().includes(lowerQuery)
    );
  },

  // Get variant statistics
  getVariantStats: (variants: ProductVariant[]) => {
    const totalVariants = variants.length;
    const inStockVariants = variants.filter(
      (v) => (v.quantity || 0) > 0
    ).length;
    const outOfStockVariants = totalVariants - inStockVariants;

    return {
      totalVariants,
      inStockVariants,
      outOfStockVariants,
      totalStock: variants.reduce((sum, v) => sum + (v.quantity || 0), 0),
      totalValue: variants.reduce(
        (sum, v) => sum + (v.price || 0) * (v.quantity || 0),
        0
      ),
      averagePrice:
        totalVariants > 0
          ? variants.reduce((sum, v) => sum + (v.price || 0), 0) / totalVariants
          : 0,
    };
  },

  // Format variant for display
  formatVariant: (variant: ProductVariant) => ({
    ...variant,
    displayPrice: `$${(variant.price || 0).toFixed(2)}`,
    displayCost: `$${(variant.cost || 0).toFixed(2)}`,
    availableQuantity: variant.quantity || 0,
    isInStock: (variant.quantity || 0) > 0,
  }),

  // Parse attributes string to object
  parseAttributes: (attributesString: string): Record<string, string> => {
    try {
      return JSON.parse(attributesString || "{}");
    } catch {
      return {};
    }
  },

  // Stringify attributes object
  stringifyAttributes: (attributes: Record<string, string>): string => {
    try {
      return JSON.stringify(attributes);
    } catch {
      return "{}";
    }
  },

  // Convert price from cents to dollars
  centsToPrice: (cents: number): number => cents / 100,

  // Convert price from dollars to cents
  priceToCents: (price: number): number => Math.round(price * 100),

  // Generate SKU suggestion
  generateSKU: (productCode: string, variantName: string): string => {
    const cleanProductCode = productCode
      .replace(/[^A-Z0-9]/gi, "")
      .toUpperCase();
    const cleanVariantName = variantName
      .replace(/[^A-Z0-9]/gi, "")
      .toUpperCase()
      .substring(0, 4);
    const timestamp = Date.now().toString().slice(-4);
    return `${cleanProductCode}-${cleanVariantName}-${timestamp}`;
  },
};
